import { HTTP_TYPES } from '@/lib/enums';
import api from '@/api/api';

import type { AuditActionsQuery } from './types/management-audit-actions.types';

// Re-export the types so consumers keep importing them from this module.
export type {
	AuditAction,
	AuditActionsQuery,
	AuditActionsPage,
} from './types/management-audit-actions.types';

const API_VERSION = 'v1';

/**
 * The audit trail lives on the intermediate management service, under the same
 * apim root as the Service Bus tooling. `VITE_DEPOT_API_ENDPOINTS` ends with a
 * trailing slash, and this resource has no sub-paths: the base IS the endpoint.
 */
const MANAGEMENT_BASE = `${import.meta.env.VITE_DEPOT_API_ENDPOINTS}management/audit-actions`;

const response = {
	/**
	 * Read a page of audited API actions.
	 * GET management/audit-actions?application=…&from=…&to=…&limit=…&continuationToken=…
	 *
	 * Only `application`, `from` and `to` are filterable server side; the other
	 * returned fields (status code, caller, method, url, correlation id) come
	 * back but cannot be queried.
	 *
	 * A `continuationToken` is only meaningful alongside the exact filters that
	 * produced it, so callers must send the frozen query, not a recomputed one.
	 */
	async getAuditActions(query: AuditActionsQuery = {}): Promise<any> {
		// Spread only the filters that hold a value, so axios omits the rest
		// instead of serialising `application=undefined` into the query string.
		const params = {
			...(query.application ? { application: query.application } : {}),
			...(query.from ? { from: query.from } : {}),
			...(query.to ? { to: query.to } : {}),
			...(typeof query.limit === 'number' ? { limit: query.limit } : {}),
			// Tokens carry `+`, `/`, `=` and sometimes raw JSON, so they must go
			// through the params object to be encoded, never concatenated in.
			...(query.continuationToken ? { continuationToken: query.continuationToken } : {}),
		};

		return await api(
			HTTP_TYPES.GET,
			MANAGEMENT_BASE,
			undefined,
			params,
			'application/json',
			API_VERSION,
		);
	},
};

export default response;
